import React, { useState, useEffect, useRef } from "react";
import './style.css';
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axios from "axios";
import { t } from "../../translation/translation";
import { TEXT_KEYS } from "../../constants/textKeys";
import TitleWithIcon from "../../components/common/TitleWithIcon";
import { Button } from "../../components/common/Button";
import { getRouteData } from "../../helpers/getRouteData";
import IMAGES_PATH from "../../Images";

const DonateForm = () => {
    const stripe = useStripe();
    const elements = useElements();

    const rootStyles = getComputedStyle(document.documentElement);
    const inputTextColor = rootStyles.getPropertyValue('--black-b').trim()
    const inputPlaceholderColor = rootStyles.getPropertyValue('--black-300').trim()
    const cardElementOptions = {
        style: {
            base: {
                color: inputTextColor, 
                fontSize: '16px', 
                '::placeholder': {
                    color: inputPlaceholderColor,
                },
            },
            invalid: {
                color: '#e5424d', 
            },
        },
    };

    const [message, setMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [clientSecret, setClientSecret] = useState("");
    const [clientSecretLoading, setClientSecretLoading] = useState(false);

    const [selectedPrice, setSelectedPrice] = useState("100");
    const [showOtherAmount, setShowOtherAmount] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        reason: "",
        amount: "",
        message: "",
        otheramt: ""
    });

    /*** CHECK TERMS OF USE LOGIC ***/
    const [checked, setChecked] = useState(false);
    const [isInvalid, setIsInvalid] = useState(false);
    const checkboxRef = useRef(null);

    const { url, routes} = getRouteData()

    const handleCheckboxChange = () => {
      setChecked(!checked);
      setIsInvalid(false); // Reset validation on user interaction
    };
  
    // Scroll to checkbox if not validated
    useEffect(() => {
      if (isInvalid && checkboxRef.current) {
        checkboxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, [isInvalid]);
    /*** CHECK TERMS OF USE LOGIC ***/
    useEffect(() => {
        setShowOtherAmount(selectedPrice === "otheramount");
    }, [selectedPrice]);


    useEffect(() => {
        const rawAmount = selectedPrice === "otheramount" ? formData.otheramt : selectedPrice;
        const amount = Number(rawAmount);      
      
        if (amount && !isNaN(amount) && Number(amount) > 0) {
          fetchClientSecret(amount);
        }
      }, [selectedPrice, formData.otheramt]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData((prevState) => ({ ...prevState, [id]: value }));
    };

    useEffect(() => {
        if (!stripe) {
            return;
        }

        const clientSecret = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret"
        );

        if (!clientSecret) {
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            switch (paymentIntent.status) {
                case "succeeded":
                    setMessage("Payment succeeded!");
                    break;
                case "processing":
                    setMessage("Your payment is processing.");
                    break;
                case "requires_payment_method":
                    setMessage("Your payment was not successful, please try again.");
                    break;
                default:
                    setMessage("Something went wrong.");
                    break;
            }
        });
    }, [stripe]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!checked) {
          setIsInvalid(true);
          return
        }

        if (!stripe || !elements) {
            return;
        }

        setIsLoading(true);

        const amount = selectedPrice === "otheramount" ? formData.otheramt : selectedPrice;

        try {
            const donationResponse = await axios.post(url + routes.save_donation, {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                reason: formData.reason,
                amount,
                message: formData.message
            });

            const donationId = donationResponse.data.donationId;

            const receivedDoodleIdFromClient = localStorage.getItem('doodleId');
            const doodleId = receivedDoodleIdFromClient;

            const payment_method_config = {
                card: elements.getElement(CardElement),
                billing_details: {
                    name: formData.name,
                    email: formData.email,
                },
            }

            if (!clientSecret) {
                console.error("Client secret not ready yet.");
                return;
            }

            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: payment_method_config,
            });

            if (error) {
                setMessage("Payment Failed");
                console.error("Payment Failed", error.message);

                await axios.post(url + routes.delete_doodle, { doodleId });

            } else {
                if (paymentIntent.status === "succeeded") {

                    // Calling /update-donation API
                    await axios.post(url + routes.update_donation, { donationId, doodleId });

                    setFormSubmitted(true);

                    // Calling /send-email API
                    await axios.post(url + routes.send_email, { email: formData.email, doodleId });

                } else {
                    console.error('Error Updating Database');
                }
            }
        } catch (error) {
            setMessage("An unexpected error occurred.");
            console.error("Error", error);
        }
        setIsLoading(false);
    };

    // Fetch client secret when needed
    const fetchClientSecret = async (amount) => {
        try {
            setClientSecretLoading(true);
            const res = await fetch(url + routes.create_payment_intent, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ amount: parseFloat(amount) }),
            });
        
            if (!res.ok) {
                throw new Error(`Failed to fetch client secret: ${res.status}`);
            }
        
            const data = await res.json();
            setClientSecret(data.clientSecret);
        } catch (error) {
            console.error("Error fetching client secret:", error);
        } finally {
            setClientSecretLoading(false);
        }
    };

    return (
        <div className="page-container">
            <TitleWithIcon
                firstTitlePart={<h2>{t(TEXT_KEYS.DONATE_FORM_TITLE)}</h2>}
            />
            {!formSubmitted ?
                <div className="section-donate-form section">
                <h4 className="section-title-2">{t(TEXT_KEYS.DONATE_FORM_FORM_TITLE)}</h4>
                <form onSubmit={handleSubmit}>
                    <div className="row-container line-container">
                        <div className="input-container">
                            <label htmlFor="name">{t(TEXT_KEYS.DONATE_FORM_INPUT_LABEL_NAME)}</label>
                            <input
                                type="text"
                                id="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder={t(TEXT_KEYS.DONATE_FORM_INPUT_PLACEHOLDER_NAME)}
                                className="form-control styled-input"
                                required
                            />
                        </div>
                        <div className="input-container">
                            <label htmlFor="email">{t(TEXT_KEYS.DONATE_FORM_INPUT_LABEL_EMAIL)}</label>
                            <input
                                type="email"
                                id="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder={t(TEXT_KEYS.DONATE_FORM_INPUT_PLACEHOLDER_EMAIL)}
                                className="form-control styled-input"
                                required
                            />
                        </div>
                    </div>
                    <div className="row-container line-container">
                        <div className="input-container">
                            <label htmlFor="phone">{t(TEXT_KEYS.DONATE_FORM_INPUT_LABEL_PHONE)}</label>
                            <input
                                type="text"
                                id="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                placeholder={t(TEXT_KEYS.DONATE_FORM_INPUT_PLACEHOLDER_PHONE)}
                                className="form-control styled-input"
                                required
                            />
                        </div>
                        <div className="input-container">
                            <label htmlFor="reason">{t(TEXT_KEYS.DONATE_FORM_ONPUT_LABEL_FIND_US)}</label>
                            <select
                                id="reason"
                                name="reason"
                                value={formData.reason}
                                onChange={handleInputChange}
                                className="form-control styled-input"
                                required
                            >
                                <option value="">{t(TEXT_KEYS.DONATE_FORM_ONPUT_SELECT_FIND_US_1)}</option>
                                <option value="Friend&Family">Friend&{t(TEXT_KEYS.DONATE_FORM_ONPUT_SELECT_FIND_US_2)}</option>
                                <option value="Social Media">{t(TEXT_KEYS.DONATE_FORM_ONPUT_SELECT_FIND_US_3)}</option>
                                <option value="Networking Event">{t(TEXT_KEYS.DONATE_FORM_ONPUT_SELECT_FIND_US_4)}</option>
                                <option value="Other">{t(TEXT_KEYS.DONATE_FORM_ONPUT_SELECT_FIND_US_5)}</option>
                            </select>
                        </div>
                    </div>
                    <div className="pledge-amount-container line-container">
                        <label>{t(TEXT_KEYS.DONATE_FORM_ONPUT_PLEDGE_AMOUNT)}</label>
                        <div className="price-button-group">
                            <div className="input-container amount-selector">
                                <div className="price-btn-container">
                                    {['10', '20', '30', '40', '50', '100', '200'].map((amount) => (
                                        <button
                                            key={amount}
                                            type="button"
                                            className={`price-button ${selectedPrice === amount ? 'selected' : ''}`}
                                            onClick={() => setSelectedPrice(amount)}
                                        >
                                            <i className="fa-solid fa-euro-sign"></i> {amount}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="input-container">
                                <button
                                    type="button"
                                    className={`price-button ${selectedPrice === 'otheramount' ? 'selected' : ''}`}
                                    onClick={() => setSelectedPrice('otheramount')}
                                    >{t(TEXT_KEYS.DONATE_FORM_ONPUT_OTHER_AMOUNT)}</button>
                            </div>
                        </div>

                        {showOtherAmount && (
                            <div id="checkother-amt">
                            <input
                                type="number"
                                id="otheramt"
                                name="otheramt"
                                value={formData.otheramt || ''}
                                onChange={handleInputChange}
                                className="form-control styled-input"
                                placeholder="Enter other amount"
                                required
                                aria-required="true"
                            />
                            </div>
                        )}
                    </div>

            
                    <div>
                        <div className="input-container line-container">
                            <label htmlFor="message">{t(TEXT_KEYS.DONATE_FORM_INPUT_LABEL_MESSAGE)}</label>
                            <textarea
                                id="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                placeholder={t(TEXT_KEYS.DONATE_FORM_INPUT_PLACEHOLDER_MESSAGE)}
                                className="form-control styled-input"
                            />
                        </div>
                        <div className="input-container line-container">
                            <label htmlFor="card-element">{t(TEXT_KEYS.DONATE_FORM_INPUT_LABEL_CREDIT_CARD)}</label>
                            <CardElement id="card-element" options={cardElementOptions} className="form-control styled-input" />
                        </div>
                    </div>
            
                    <div className="checkbox-container line-container">
                        <div>
                            <input
                                type="checkbox"
                                id="custom-checkbox"
                                ref={checkboxRef}
                                checked={checked}
                                onChange={handleCheckboxChange}
                                className={`checkbox-input ${isInvalid ? 'invalid' : ''}`}
                            />
                            <label htmlFor="custom-checkbox" className={`checkbox-label ${isInvalid ? 'invalid' : ''}`}>
                                {t(TEXT_KEYS.DONATE_FORM_INPUT_TERM_OF_USE)}
                            </label>
                        </div>
                        <div>
                            <img src={IMAGES_PATH.IMAGE_STRIPE} alt="Powered by stripe" className="stripe-image"/>
                        </div>
                    </div>
            
                    <div>
                        <Button 
                            label={t(TEXT_KEYS.SUBMIT)}
                            htmlType="submit"
                            disabled={!clientSecret || clientSecretLoading || isLoading}
                        />
                        {message && <div id="payment-message">{message}</div>}
                    </div>
                </form>
            </div>
            
            :
                <div className={'section-donate-form-congrats-container section'}>
                    <h4 className="section-donate-form-congrats-container-title">Thank you for your generous donation!<br />
                        🎉Congratulations on creating a doodle!🎨</h4>
                    <div className="section-donate-form-congrats-container-description">
                        <p>Your artistic expression is not only beautiful but also impactful. Every stroke of your pen contributes to positive change. Thank you for turning creativity into kindness!</p>
                        <p>Kindly review your inbox at <a className="congo-link" href='/'>{formData.email}</a> to access the doodle image. Thank you!</p>
                    </div>
                </div>
            }
        </div>
    );
}

export default DonateForm;